/**
 * HTML response parser.
 *
 * Consumes a raw HTML string (full-response path) and extracts
 * everything the content swapper and asset loader need:
 *
 * - The main content region, matched against the content selector
 *   chain from config.
 * - The parsed `<title>` text, used to update document.title.
 * - The parsed `drupalSettings` JSON, used to replace the current
 *   settings via the drupal-settings merge helper.
 * - The list of stylesheet hrefs the target page declares.
 * - The script sources the target page declares, each carrying its
 *   `type` so ES-module chunks are re-injected as modules.
 * - The set of SDX component IDs declared inside the content
 *   region, so the asset loader can dynamic-import their bundles
 *   before the DOM swap to avoid mount-time race conditions.
 *
 * A sibling helper extracts the SDX page data payload that the
 * theme engine embeds in a `<script type="application/json"
 * data-sdx-props-for="...">` tag next to the mount point.
 */

import type { ParsedPage } from './types';
import type { AssetInventory } from './asset-inventory';
import type { ScriptRef } from './asset-loader';

export function parsePage(html: string, contentSelector: string, inventory: AssetInventory): ParsedPage {
  const doc = new DOMParser().parseFromString(html, 'text/html');

  const content = findContent(doc, contentSelector);
  const titleEl = doc.querySelector('title');
  const title = titleEl?.textContent ?? null;

  const settingsEl = doc.querySelector<HTMLScriptElement>(
    'script[data-drupal-selector="drupal-settings-json"]',
  );
  const settings = parseJsonScript(settingsEl);

  const newStyles: string[] = [];
  doc.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]').forEach((el) => {
    if (el.href && !inventory.hasStylesheet(el.href)) {
      newStyles.push(el.href);
    }
  });

  // Carry each script's type: an ES-module chunk injected as a bare
  // <script> crashes with "Cannot use import statement outside a
  // module". nomodule fallbacks are for browsers this runtime does
  // not support, and data scripts (application/json etc.) are not
  // loadable code - both are skipped.
  const newScripts: ScriptRef[] = [];
  doc.querySelectorAll<HTMLScriptElement>('script[src]:not([nomodule])').forEach((el) => {
    const type = (el.getAttribute('type') ?? '').toLowerCase();
    if (type && type !== 'module' && type !== 'text/javascript' && type !== 'application/javascript') {
      return;
    }
    if (el.src && !inventory.hasScript(el.src)) {
      newScripts.push(type === 'module' ? { src: el.src, type: 'module' } : { src: el.src });
    }
  });

  const componentIds = collectComponentIds(content);

  return { doc, content, title, settings, newStyles, newScripts, componentIds };
}

/**
 * Pulls the SDX page data JSON out of the envelope script the
 * server emits alongside the mount point.
 *
 * `PageRenderer` emits the envelope as a preceding sibling of the
 * mount element with a distinct `data-sdx-envelope` attribute so it
 * can never be confused with a nested widget's own
 * `data-sdx-props-for` script (widgets now receive props in memory
 * from their scaffold, but Twig-placeholder widgets and cached pages
 * can still carry per-widget scripts).
 *
 * Returns null when the response is not an SDX-engine page, when
 * the envelope is missing, or when its JSON fails to parse. The
 * caller treats null as "fall back to a full document load" - the
 * envelope marker is a load-bearing contract, not a hint.
 */
export function extractSdxPageData(
  doc: Document,
): Record<string, unknown> | null {
  const envelope = doc.querySelector<HTMLScriptElement>(
    'script[type="application/json"][data-sdx-envelope]',
  );
  const parsed = parseJsonScript(envelope);
  if (!parsed) return null;

  const pageData = (parsed as { pageData?: Record<string, unknown> }).pageData;
  return pageData ?? parsed;
}

function findContent(doc: Document, selectorChain: string): Element | null {
  for (const selector of selectorChain.split(',')) {
    const match = doc.querySelector(selector.trim());
    if (match) return match;
  }
  return null;
}

function collectComponentIds(content: Element | null): string[] {
  if (!content) return [];
  const ids = new Set<string>();
  content.querySelectorAll<HTMLElement>('.sdx-component[data-component]').forEach((el) => {
    const id = el.getAttribute('data-component');
    if (id) ids.add(id);
  });
  return Array.from(ids);
}

function parseJsonScript(element: HTMLScriptElement | null): Record<string, unknown> | null {
  if (!element?.textContent) return null;
  try {
    const parsed = JSON.parse(element.textContent);
    return typeof parsed === 'object' && parsed !== null ? parsed : null;
  }
  catch {
    return null;
  }
}
